import { format } from "date-fns";
import intl from "react-intl-universal";
import { TbChevronRight } from "react-icons/tb";
import {
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
} from "../../../../../Components/Molecules/GridHistory";
import { formatCurrency } from "../../../../../Utils/formatters";
import { getPixChargeStatusMeta, getPixChargeTypeMeta } from "../pixChargesFilters";

const GRID_COLS =
  "lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_minmax(0,1.4fr)_minmax(0,0.85fr)_minmax(5.5rem,0.95fr)_auto]";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return "—";
  }
};

const formatTxid = (value) => {
  if (!value) return "—";
  if (value.length <= 20) return value;
  return `${value.slice(0, 10)}…${value.slice(-6)}`;
};

const TypeBadge = ({ type }) => {
  const meta = getPixChargeTypeMeta(type);
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.className}`}
    >
      {meta.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const meta = getPixChargeStatusMeta(status);
  return (
    <span
      className={`inline-flex w-[5.5rem] shrink-0 items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.className}`}
    >
      {meta.label}
    </span>
  );
};

const ChargesHeaderRow = () => (
  <GridHistoryHeader colSpan={6}>
    <span>{intl.get("pix.charges.type")}</span>
    <span>{intl.get("grid.date")}</span>
    <span>{intl.get("pix.charges.txid")}</span>
    <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>{intl.get("grid.value")}</span>
    <span>{intl.get("grid.status")}</span>
    <span className="sr-only">{intl.get("common.details")}</span>
  </GridHistoryHeader>
);

const ChargeRow = ({ charge, onSelect }) => (
  <GridHistoryRow
    colSpan={6}
    className="cursor-pointer hover:bg-gray-50/80 dark:hover:bg-brand-dark-elevated/50"
    onClick={() => onSelect(charge)}
    role="button"
    tabIndex={0}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect(charge);
      }
    }}
  >
    <GridHistoryCell label={intl.get("pix.charges.type")}>
      <TypeBadge type={charge.type} />
    </GridHistoryCell>

    <GridHistoryCell label={intl.get("grid.date")}>
      <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(charge.createdAt)}</p>
    </GridHistoryCell>

    <GridHistoryCell label={intl.get("pix.charges.txid")}>
      <p className="truncate font-mono text-sm text-brand-dark dark:text-gray-100" title={charge.txid}>
        {formatTxid(charge.txid)}
      </p>
    </GridHistoryCell>

    <GridHistoryCell label={intl.get("grid.value")} align="right">
      <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
        {formatCurrency(charge.amount)}
      </p>
    </GridHistoryCell>

    <GridHistoryCell label={intl.get("grid.status")}>
      <StatusBadge status={charge.status} />
    </GridHistoryCell>

    <div className="flex items-center justify-end">
      <TbChevronRight className="h-4 w-4 text-gray-400" aria-hidden />
    </div>
  </GridHistoryRow>
);

export const PixChargesList = ({ items, onSelectCharge, footer }) => {
  if (!items.length) {
    return (
      <div className="app-card overflow-hidden">
        <div className="app-empty-state m-4">{intl.get("pix.charges.noResults")}</div>
      </div>
    );
  }

  return (
    <div className="app-card overflow-hidden">
      <GridHistoryContainer gridCols={GRID_COLS}>
        <ChargesHeaderRow />
        {items.map((charge) => (
          <ChargeRow key={charge.id} charge={charge} onSelect={onSelectCharge} />
        ))}
      </GridHistoryContainer>
      {footer}
    </div>
  );
};
